import React, { useState } from 'react';
import { X, Mail, Sparkles, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import type { Connection } from './ConnectionModal';

interface EmailSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection: Connection;
}

const EmailSummaryModal: React.FC<EmailSummaryModalProps> = ({ isOpen, onClose, connection }) => {
  const [prompt, setPrompt] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const defaultEmailTemplate = `Connection Summary for ${connection.full_name}

Email: ${connection.email_address}

ICP (Ideal Customer Profile):
${connection.icp?.trim() || 'No ICP on file'}

Call Me When Statements:

1. ${connection.callMeWhen1?.trim() || 'Not set'}

2. ${connection.callMeWhen2?.trim() || 'Not set'}

3. ${connection.callMeWhen3?.trim() || 'Not set'}
`;

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt for the AI');
      return;
    }

    setIsGenerating(true);
    try {
      const systemPrompt = `You are an expert email writer. Use the following connection information to craft a professional, personalized email based on the user's request.

Connection Information:
- Name: ${connection.full_name}
- Email: ${connection.email_address}
${connection.role?.trim() ? `- Role: ${connection.role}` : ''}
${connection.linkedin_url?.trim() ? `- LinkedIn: ${connection.linkedin_url}` : ''}

ICP (Ideal Customer Profile):
${connection.icp?.trim() || 'No ICP on file'}

Call Me When Statements:
1. ${connection.callMeWhen1?.trim() || 'Not set'}
2. ${connection.callMeWhen2?.trim() || 'Not set'}
3. ${connection.callMeWhen3?.trim() || 'Not set'}

User's Request: ${prompt}

Generate a professional email that addresses the user's request. Include an appropriate subject line in the format "Subject: [subject text]" at the top, followed by the email body.`;

      const { data, error } = await supabase.functions.invoke('transcript-prompt', {
        body: {
          prompt: systemPrompt,
          transcriptId: null,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const response = data?.response || '';
      setGeneratedEmail(response);
      toast.success('Email generated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate email';
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendToGmail = () => {
    const emailContent = generatedEmail || defaultEmailTemplate;

    let subject = `Summary: ${connection.full_name}`;
    let body = emailContent;

    const subjectMatch = emailContent.match(/^Subject:\s*(.+?)$/m);
    if (subjectMatch) {
      subject = subjectMatch[1].trim();
      body = emailContent.replace(/^Subject:\s*.+?\n\n?/m, '');
    }

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(connection.email_address)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-4xl flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Email Summary</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{connection.full_name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              AI Prompt
            </label>
            <div className="flex gap-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Write a friendly follow-up email asking about their interest in our product..."
                rows={3}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
                className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0 h-fit"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Generate
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Preview
            </label>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50 p-4">
              <textarea
                value={generatedEmail || defaultEmailTemplate}
                onChange={(e) => setGeneratedEmail(e.target.value)}
                rows={16}
                className="w-full px-0 py-0 border-0 bg-transparent text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-0 resize-none"
              />
            </div>
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSendToGmail}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Open in Gmail
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSummaryModal;
